import { Link, useLocation } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { ChevronRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";

/** Shopping screens where the cart bar is shown (not on cart, checkout, account…). */
export function isShoppingPath(pathname: string) {
  return pathname === "/" || pathname.startsWith("/categories") || pathname === "/search" || pathname.startsWith("/products/") || pathname.startsWith("/packs/");
}

/**
 * Mobile sticky cart bar, just above the bottom navigation: distinct products,
 * cart total (priced by the server quote) and a link to the cart / checkout flow.
 * Hidden when the cart is empty.
 */
export function CartBar() {
  const { t, money } = useI18n();
  const { count, quote, quoteFetching } = useCart();
  const { pathname } = useLocation();
  const visible = count > 0 && isShoppingPath(pathname);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 36 }}
          className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 px-3 pb-2 md:hidden"
        >
          <Link
            to="/cart"
            data-cart-bar=""
            className="flex h-14 items-center gap-3 rounded-2xl bg-brand-600 ps-3 pe-2 text-white shadow-[var(--shadow-float)] transition active:scale-[0.98]"
            aria-label={`${t("cart.view")} — ${t("cart.items", { count })}${quote ? `, ${money(quote.subtotalCents)}` : ""}`}
          >
            <span className="relative flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
              <ShoppingBag className="size-5" />
              <span className="ltr-nums absolute -end-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-saffron-400 px-1 text-[11px] font-bold text-ink">
                {count > 99 ? "99+" : count}
              </span>
            </span>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-xs text-white/80">{t("cart.items", { count })}</span>
              <span className={`ltr-nums block font-extrabold transition-opacity ${quoteFetching ? "opacity-70" : ""}`}>
                {quote ? money(quote.subtotalCents) : "…"}
              </span>
            </span>
            <span className="flex h-10 shrink-0 items-center gap-1 rounded-xl bg-white px-3 text-sm font-bold text-brand-700">
              {t("cart.view")}
              <ChevronRight className="size-4 rtl:rotate-180" />
            </span>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
